import {createStyles} from '@mantine/core';
import React, {ReactNode, useCallback, useEffect, useRef, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {FLOWCHART_SIDEPANEL_Z_INDEX} from 'ui-library/reactflow/sidepanels/FlowchartSidebar';
import {useResizableSidepanel} from 'ui-library/reactflow/sidepanels/useResizableSidepanel';

export enum SidepanelStateChangeAction {
  OPEN = 'open',
  CLOSE = 'close',
  EXPAND = 'expand',
  COLLAPSE = 'collapse',
  SWITCH_TAB = 'switch_tab',
}

export enum PanelSide {
  LEFT = 'left',
  RIGHT = 'right',
}

export enum SidepanelSize {
  EXTRA_SMALL = 'extra_small',
  SMALL = 'small',
  MEDIUM = 'medium',
  FULL = 'full',
}

/**
 * The width of the sidepanel in pixels.
 *
 * For any size other than FULL, use numbers so that the value can be used
 * in calculations.
 */
export const sizeToWidth: Record<SidepanelSize, string | number> = {
  [SidepanelSize.EXTRA_SMALL]: 240,
  [SidepanelSize.SMALL]: 320,
  [SidepanelSize.MEDIUM]: 640,
  [SidepanelSize.FULL]: '100%',
};

export interface FlowchartContextPanelProps {
  panelSide: PanelSide;
  isOpen: boolean;
  size?: SidepanelSize;
  onClose: () => void;
  sidepanelName: string;
  headerContent?: ReactNode;
  /** Content to render inside the sidepanel */
  children: ReactNode;
  showExpandCollapseButtons?: boolean;
  sidepanelKey?: string;
  /** Flowchart type for analytics tracking. When provided, analytics are tracked automatically. */
  flowchartType?: string;
  onPanelSizeChange?: (size: SidepanelSize) => void;
  outerRef?: React.RefObject<HTMLDivElement>;
  /** When true, adds a drag handle to resize the panel width. Only applies to right panels. */
  resizable?: boolean;
  /** localStorage key to persist the resized width. Defaults to 'flowchart-sidepanel-width'. */
  resizeStorageKey?: string;
  /** Ref to the container element used to calculate max resize width. */
  resizeContainerRef?: React.RefObject<HTMLElement>;
}

function getClassForSidepanelSize(
  size: SidepanelSize,
  classes: ReturnType<typeof useStyles>['classes']
): string {
  switch (size) {
    case SidepanelSize.FULL:
      return classes.sizeFull;
    case SidepanelSize.EXTRA_SMALL:
      return classes.sizeExtraSmall;
    case SidepanelSize.SMALL:
      return classes.sizeSmall;
    case SidepanelSize.MEDIUM:
    default:
      return classes.sizeMedium;
  }
}

/**
 * A sidepanel to use with ReactFlow flowcharts, supports left and right side panels
 * at different widths (including full width).
 *
 * Can use with @link{FlowchartSidebar}.
 *
 * @example
 * <FlowchartContextPanel
 *   panelSide={PanelSide.RIGHT}
 *   isOpen={isOpen}
 *   size={SidepanelSize.FULL}
 *   onClose={() => setIsOpen(false)}
 * >
 *   Your content here
 * </FlowchartContextPanel>
 *
 * @example
 * <FlowchartContextPanel
 *   panelSide={PanelSide.LEFT}
 *   isOpen={isOpen}
 *   size={SidepanelSize.MEDIUM}
 *   onClose={() => setIsOpen(false)}
 * >
 *   Your content here
 * </FlowchartContextPanel>
 */
export function FlowchartContextPanel({
  panelSide,
  isOpen,
  size = SidepanelSize.MEDIUM,
  onClose,
  children,
  sidepanelName,
  headerContent,
  showExpandCollapseButtons = true,
  sidepanelKey,
  flowchartType,
  onPanelSizeChange,
  outerRef,
  resizable = false,
  resizeStorageKey,
  resizeContainerRef,
}: FlowchartContextPanelProps): JSX.Element | null {
  const {classes, cx} = useStyles();
  const internalRef = useRef<HTMLDivElement>(null);
  const panelRef = outerRef ?? internalRef;
  const prevIsOpenRef = useRef(isOpen);
  const [panelSize, setPanelSize] = useState<SidepanelSize>(size);

  const isResizableActive = resizable && panelSide === PanelSide.RIGHT && panelSize !== SidepanelSize.FULL;
  const defaultWidthForResize = typeof sizeToWidth[size] === 'number' ? sizeToWidth[size] : 640;
  const {
    width: resizedWidth,
    isResizing,
    handleMouseDown: handleResizeMouseDown,
  } = useResizableSidepanel({
    storageKey: resizeStorageKey ?? 'flowchart-sidepanel-width',
    defaultWidth: defaultWidthForResize,
    enabled: isResizableActive,
    containerRef: resizeContainerRef,
  });

  const trackStateChange = useCallback(
    (action: SidepanelStateChangeAction) => {
      if (flowchartType) {
        analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.SIDEPANEL_VISIBILITY_STATE_CHANGED, {
          actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
          sidepanelAction: action,
          sidepanelName,
          panelSide,
          flowchartType,
        });
      }
    },
    [flowchartType, sidepanelName, panelSide]
  );

  useEffect(() => {
    setPanelSize(size);
    onPanelSizeChange?.(size);
  }, [size, setPanelSize, onPanelSizeChange]);

  // Track OPEN state changes
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      trackStateChange(SidepanelStateChangeAction.OPEN);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, trackStateChange]);

  const handleExpand = useCallback(() => {
    setPanelSize(SidepanelSize.FULL);
    onPanelSizeChange?.(SidepanelSize.FULL);
    trackStateChange(SidepanelStateChangeAction.EXPAND);
  }, [setPanelSize, onPanelSizeChange, trackStateChange]);

  const handleCollapse = useCallback(() => {
    setPanelSize(size);
    onPanelSizeChange?.(size);
    trackStateChange(SidepanelStateChangeAction.COLLAPSE);
  }, [setPanelSize, size, onPanelSizeChange, trackStateChange]);

  const handleClose = useCallback(() => {
    trackStateChange(SidepanelStateChangeAction.CLOSE);
    onClose();
  }, [onClose, trackStateChange]);

  // Close sidepanel when Escape key is pressed.
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') {
        return;
      }

      if (event.defaultPrevented) {
        return;
      }

      // Check if focus is within the sidepanel, or if no specific element has focus
      const target = event.target as HTMLElement | null;
      const activeElement = document.activeElement;
      const focusWithinSidepanel = target != null && panelRef.current?.contains(target);
      const noSpecificFocus =
        activeElement == null ||
        activeElement === document.body ||
        activeElement === document.documentElement;

      if (focusWithinSidepanel || noSpecificFocus) {
        event.preventDefault();
        event.stopPropagation();
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleClose, panelRef]);

  const sizeClass = getClassForSidepanelSize(panelSize, classes);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      ref={panelRef}
      className={cx(
        classes.sidepanel,
        !isResizableActive && sizeClass,
        panelSide === PanelSide.RIGHT && classes.rightSidepanel,
        panelSide === PanelSide.LEFT && classes.leftSidepanel,
        isResizing && classes.noTransition
      )}
      style={isResizableActive ? {width: resizedWidth} : undefined}
      {...getTestProps({
        component: 'FlowchartContextPanel',
        element: 'root',
        key: sidepanelKey,
      })}
    >
      {isResizableActive && (
        <div
          className={classes.resizeHandle}
          onMouseDown={handleResizeMouseDown}
          {...getTestProps({
            component: 'FlowchartContextPanel',
            element: 'resizeHandle',
          })}
        >
          <div className={classes.dragHandleContainer}>
            <BenchlingIcon className={classes.dragHandleIcon} icon="drag-handle" />
          </div>
        </div>
      )}
      <div
        className={classes.header}
        {...getTestProps({
          component: 'FlowchartContextPanel',
          element: 'header',
        })}
      >
        <div
          className={classes.headerLeft}
          {...getTestProps({
            component: 'FlowchartContextPanel',
            element: 'headerLeft',
          })}
        >
          {panelSide === PanelSide.RIGHT && showExpandCollapseButtons && (
            <HeaderExpandCollapseButton
              classes={classes}
              inputSize={size}
              currentPanelSize={panelSize}
              handleExpand={handleExpand}
              handleCollapse={handleCollapse}
            />
          )}
          <h2
            className={classes.sidepanelName}
            {...getTestProps({
              component: 'FlowchartContextPanel',
              element: 'sidepanelName',
            })}
          >
            {sidepanelName}
          </h2>
          {headerContent}
        </div>
        <HeaderCloseButton
          classes={classes}
          panelSide={panelSide}
          onClose={handleClose}
          currentPanelSize={panelSize}
        />
      </div>
      <div
        className={classes.content}
        {...getTestProps({
          component: 'FlowchartContextPanel',
          element: 'content',
        })}
      >
        {children}
      </div>
    </div>
  );
}

export default FlowchartContextPanel;

// Re-export ContextPanelContent for convenience
export {ContextPanelContent} from './ContextPanelContent';

function HeaderCloseButton(props: {
  classes: ReturnType<typeof useStyles>['classes'];
  panelSide: PanelSide;
  currentPanelSize: SidepanelSize;
  onClose: () => void;
}): JSX.Element {
  const {classes} = props;
  const isRightPanel = props.panelSide === PanelSide.RIGHT;
  const closeIcon = isRightPanel ? 'close' : 'chevron-left';

  return (
    <div className={classes.headerButtons}>
      <button
        onClick={props.onClose}
        aria-label="Close panel"
        {...getTestProps({
          component: 'FlowchartContextPanel',
          element: 'closeButton',
        })}
        className={classes.closeButton}
        type="button"
      >
        <BenchlingIcon icon={closeIcon} />
        {props.currentPanelSize === SidepanelSize.FULL && (
          <span
            className={classes.closeButtonText}
            {...getTestProps({component: 'FlowchartContextPanel', element: 'closeButtonText'})}
          >
            Close
          </span>
        )}
      </button>
    </div>
  );
}

function HeaderExpandCollapseButton(props: {
  classes: ReturnType<typeof useStyles>['classes'];
  inputSize: SidepanelSize;
  currentPanelSize: SidepanelSize;
  handleExpand: () => void;
  handleCollapse: () => void;
}): JSX.Element | null {
  const {classes} = props;
  if (props.inputSize === SidepanelSize.FULL) {
    return null;
  }
  return props.currentPanelSize === SidepanelSize.FULL ? (
    <button
      onClick={props.handleCollapse}
      aria-label="Collapse panel"
      {...getTestProps({
        component: 'FlowchartContextPanel',
        element: 'collapseButton',
      })}
      className={classes.expandButton}
      type="button"
    >
      <BenchlingIcon icon="chevron-right" />
    </button>
  ) : (
    <button
      onClick={props.handleExpand}
      aria-label="Expand panel"
      {...getTestProps({
        component: 'FlowchartContextPanel',
        element: 'expandButton',
      })}
      className={classes.expandButton}
      type="button"
    >
      <BenchlingIcon icon="chevron-left" />
    </button>
  );
}

export const useTabPanelStyles = createStyles(() => ({
  noSpacing: {
    margin: 0,
    padding: 0,
  },
}));

const useStyles = createStyles((theme) => ({
  sidepanel: {
    height: '100%',
    backgroundColor: theme.white,
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'visible',
    position: 'relative' as const,
    top: 0,
    zIndex: FLOWCHART_SIDEPANEL_Z_INDEX,
  },
  rightSidepanel: {
    borderLeft: `1px solid ${theme.colors.gray[2]}`,
    right: 0,
    position: 'absolute' as const,
  },
  leftSidepanel: {
    borderRight: `1px solid ${theme.colors.gray[2]}`,
    left: 0,
    position: 'relative' as const,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  sidepanelName: {
    margin: 0,
    fontSize: '13px',
    fontWeight: 600,
  },
  headerButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  expandButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px',
    '&:hover': {
      backgroundColor: theme.colors.gray[1],
    },
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '4px 8px',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: theme.colors.gray[1],
    },
  },
  closeButtonText: {
    fontSize: '13px',
    fontWeight: 600,
    color: theme.colors.gray[9],
  },
  content: {
    flex: 1,
    overflow: 'auto',
  },
  sizeFull: {
    width: sizeToWidth[SidepanelSize.FULL],
  },
  sizeExtraSmall: {
    width: sizeToWidth[SidepanelSize.EXTRA_SMALL],
  },
  sizeSmall: {
    width: sizeToWidth[SidepanelSize.SMALL],
  },
  sizeMedium: {
    width: sizeToWidth[SidepanelSize.MEDIUM],
  },
  resizeHandle: {
    position: 'absolute' as const,
    left: 0,
    top: 0,
    bottom: 0,
    width: 6,
    cursor: 'col-resize',
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: theme.colors.blue[4],
    },
  },
  dragHandleContainer: {
    position: 'relative' as const,
    left: -3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 5px',
    borderRadius: 3,
    backgroundColor: theme.colors.gray[2],
    cursor: 'col-resize',
  },
  dragHandleIcon: {
    color: theme.colors.gray[8],
    fontSize: 12,
  },
  noTransition: {
    transition: 'none',
  },
}));
